import type { RequestContext } from "../../../application/context/request-context.js";
import type { RdoRepository } from "../../../repositories/operational-repositories.js";
import type { Rdo, RdoConsolidated, RdoFilterOptions } from "../../../domain/rdo/rdo-domain.js";
import type { RdoId, RdoConsolidatedId } from "../../../shared/kernel/identifiers.js";
import { AppError, ErrorCode } from "../../../shared/errors/app-error.js";

function getActiveClientId(context: RequestContext): string {
  if (!context.activeClientId) {
    throw new AppError(ErrorCode.Authorization, 'Cliente ativo não informado no contexto de autenticação');
  }
  return context.activeClientId;
}

/**
 * Sentinela emitido pela apresentação quando nenhum cliente está selecionado.
 * Não é um cliente: recortar por ele devolve vazio.
 */
const NO_CLIENT_SENTINEL = 'cli-default';

/**
 * Se o apontamento está no recorte de cliente da requisição.
 *
 * Sem cliente real no contexto, não há recorte — todos passam. Esta regra
 * estava só no find(): o findById() comparava o sentinela direto contra o
 * clientId do registro e devolvia null, então a listagem mostrava o
 * apontamento e o clique em Editar não o encontrava.
 */
function matchesClientScope(context: RequestContext, rdo: Rdo): boolean {
  const activeClientId = context.activeClientId;
  if (!activeClientId || activeClientId === NO_CLIENT_SENTINEL) return true;
  return rdo.clientId === activeClientId;
}

export class InMemoryRdoRepository implements RdoRepository {
  private rdos = new Map<string, Rdo>();
  private consolidations = new Map<string, RdoConsolidated>();

  constructor() {
    this.load();
  }

  private load(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        const rData = localStorage.getItem("inspect_rdos");
        if (rData) this.rdos = new Map(JSON.parse(rData));
        const cData = localStorage.getItem("inspect_rdo_consolidations");
        if (cData) this.consolidations = new Map(JSON.parse(cData));
      }
    } catch (e) {
      console.warn("Failed to load RDOs from localStorage", e);
    }
  }

  private persist(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem("inspect_rdos", JSON.stringify(Array.from(this.rdos.entries())));
        localStorage.setItem("inspect_rdo_consolidations", JSON.stringify(Array.from(this.consolidations.entries())));
      }
    } catch (e) {
      console.warn("Failed to persist RDOs to localStorage", e);
    }
  }

  async findById(context: RequestContext, id: RdoId): Promise<Rdo | null> {
    const rdo = this.rdos.get(id);
    if (!rdo || rdo.deletedAt) {
      return null;
    }
    if (!matchesClientScope(context, rdo)) {
      return null;
    }
    return rdo;
  }

  async find(context: RequestContext, filters: RdoFilterOptions = {}): Promise<readonly Rdo[]> {
    let list = Array.from(this.rdos.values()).filter(r => !r.deletedAt);

    if (filters.clientId) {
      list = list.filter(r => r.clientId === filters.clientId);
    } else {
      // Mesma regra de recorte do findById — as duas leituras precisam
      // concordar sobre o que o sentinela significa.
      list = list.filter(r => matchesClientScope(context, r));
    }
    if (filters.startDate) {
      list = list.filter(r => r.date >= filters.startDate!);
    }
    if (filters.endDate) {
      list = list.filter(r => r.date <= filters.endDate!);
    }
    if (filters.workOrderId) {
      list = list.filter(r => r.workOrderId === filters.workOrderId);
    }
    if (filters.subWorkOrderId) {
      list = list.filter(r => r.subWorkOrderId === filters.subWorkOrderId);
    }
    if (filters.serviceFrontId) {
      list = list.filter(r => r.serviceFrontId === filters.serviceFrontId);
    }
    if (filters.projectId) {
      list = list.filter(r => r.projectId === filters.projectId);
    }
    if (filters.professionalId) {
      list = list.filter(r => r.professionalId === filters.professionalId);
    }
    if (filters.functionName) {
      const fnLower = filters.functionName.toLowerCase();
      list = list.filter(r => r.functionName.toLowerCase().includes(fnLower));
    }
    if (filters.shift) {
      list = list.filter(r => r.shift === filters.shift);
    }
    if (filters.status) {
      list = list.filter(r => r.status === filters.status);
    }

    return list;
  }

  async findByPeriodAndFront(context: RequestContext, serviceFrontId: string, startDate: string, endDate: string): Promise<readonly Rdo[]> {
    const clientId = getActiveClientId(context);
    return Array.from(this.rdos.values()).filter(r => 
      r.clientId === clientId &&
      !r.deletedAt &&
      r.serviceFrontId === serviceFrontId &&
      r.date >= startDate &&
      r.date <= endDate
    );
  }

  async listAll(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly Rdo[]> {
    const clientId = getActiveClientId(context);
    return Array.from(this.rdos.values()).filter(r =>
      r.clientId === clientId &&
      !r.deletedAt &&
      (!options?.projectId || (r as any).projectId === options.projectId)
    );
  }

  async save(context: RequestContext, rdo: Rdo): Promise<void> {
    const clientId = getActiveClientId(context);
    if (rdo.clientId !== clientId) {
      throw new AppError(ErrorCode.Authorization, 'Não é permitido gravar RDO de outro cliente (Tenant Isolation)');
    }
    this.rdos.set(rdo.id, rdo);
    this.persist();
  }

  async delete(context: RequestContext, id: RdoId, reason?: string): Promise<void> {
    const clientId = getActiveClientId(context);
    const existing = this.rdos.get(id);
    if (!existing || existing.clientId !== clientId) {
      throw new AppError(ErrorCode.NotFound, 'RDO não encontrado ou acesso negado');
    }
    if (existing.status === 'ISSUED') {
      throw new AppError(ErrorCode.Conflict, 'RDO com status EMITIDO não pode ser excluído diretamente');
    }

    const updated: Rdo = {
      ...existing,
      status: 'CANCELLED',
      deletedAt: new Date().toISOString() as any,
      deletedBy: context.userId,
      deletionReason: reason || 'Cancelado pelo usuário',
      version: existing.version + 1,
      updatedAt: new Date().toISOString() as any
    };

    this.rdos.set(id, updated);
    this.persist();
  }

  async saveConsolidated(context: RequestContext, rdo: RdoConsolidated): Promise<void> {
    const clientId = getActiveClientId(context);
    if (rdo.clientId !== clientId) {
      throw new AppError(ErrorCode.Authorization, 'Não é permitido gravar Consolidação de outro cliente');
    }
    this.consolidations.set(rdo.id, rdo);
    this.persist();
  }

  async findConsolidatedById(context: RequestContext, id: RdoConsolidatedId): Promise<RdoConsolidated | null> {
    const clientId = getActiveClientId(context);
    const item = this.consolidations.get(id);
    if (!item || item.clientId !== clientId) {
      return null;
    }
    return item;
  }

  async listConsolidated(context: RequestContext): Promise<readonly RdoConsolidated[]> {
    const clientId = getActiveClientId(context);
    return Array.from(this.consolidations.values()).filter(c => c.clientId === clientId);
  }

  async listAllRevisions(context: RequestContext, rdoId: RdoId): Promise<readonly RdoConsolidated[]> {
    const list = Array.from(this.consolidations.values())
      .filter(c => c.rdoIds.includes(rdoId))
      .sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    return list;
  }
}
